// Helpers partagés pour construire des étapes de guide (Driver.js).
import type { DriveStep } from 'driver.js';
import { navThen } from './mobileTourNav';
import type { TourLayoutShell } from './types';

export function step(
  selector: string,
  title: string,
  description: string,
  side: 'top' | 'bottom' | 'left' | 'right' = 'bottom'
): DriveStep {
  return {
    element: selector,
    popover: { title, description, side, align: 'start' },
  };
}

/** N’ajoute l’étape que si l’élément est déjà dans le DOM.
 * ⚠️ Ne pas utiliser pour des étapes après navigation (Aide → module) :
 * au build l’élément est absent → étape sautée à tort. Préférer `step()` + skipMissingElement. */
export function optionalPresentStep(
  selector: string,
  title: string,
  description: string,
  side: 'top' | 'bottom' | 'left' | 'right' = 'bottom'
): DriveStep | null {
  if (typeof document === 'undefined') return null;
  if (!document.querySelector(selector)) return null;
  return step(selector, title, description, side);
}

export function compactSteps(steps: Array<DriveStep | null | undefined | false>): DriveStep[] {
  return steps.filter(Boolean) as DriveStep[];
}

export type TourAnchor = {
  selector: string;
  title: string;
  description: string;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /**
   * true (défaut) : n’inclut l’étape que si l’élément est présent au build (filtres page).
   * false : toujours inclus (champs après ouverture drawer — skipMissingElement / waitForElement).
   */
  requirePresent?: boolean;
};

/** Convertit une liste d’ancres en étapes Driver. */
export function anchorsToSteps(anchors: TourAnchor[]): DriveStep[] {
  return compactSteps(
    anchors.map((a) => {
      const side = a.side ?? 'bottom';
      if (a.requirePresent === false) {
        return step(a.selector, a.title, a.description, side);
      }
      return optionalPresentStep(a.selector, a.title, a.description, side);
    })
  );
}

/**
 * Bouton « Créer / Ajouter » : au Suivant, ouvre le drawer/formulaire puis enchaîne.
 * Toujours inclus (même hors page) : waitForElement / skipMissingElement gèrent l’absence (permissions).
 */
export function buildCreateOpenStep(
  openSelector: string,
  title: string,
  description: string,
  side: 'top' | 'bottom' | 'left' | 'right' = 'bottom'
): DriveStep {
  return {
    element: openSelector,
    popover: {
      title,
      description,
      side,
      align: 'start',
      onNextClick: (_e, _s, { driver: drv }) => {
        const el = document.querySelector(openSelector) as HTMLElement | null;
        el?.click();
        window.setTimeout(() => drv.moveNext(), 600);
      },
    },
  };
}

/**
 * Terminé uniquement sur la dernière étape du tableau.
 * (skipMissingElement peut tromper le libellé natif de driver.js)
 */
export function syncTourNextDoneButton(opts: {
  wrapper: HTMLElement;
  activeIndex: number;
  stepsLength: number;
  nextBtnText: string;
  doneBtnText: string;
}): void {
  const { wrapper, activeIndex, stepsLength, nextBtnText, doneBtnText } = opts;
  const btn = wrapper.querySelector('.driver-popover-next-btn') as HTMLElement | null;
  if (!btn) return;
  const isLast = activeIndex >= stepsLength - 1;
  btn.textContent = isLast ? doneBtnText : nextBtnText;
}

export type NavHighlightSpec = {
  /** Sous-menu parent (ex. Stock) — 1re étape si différent du leaf. */
  parentSelector?: string;
  parentTitle: string;
  parentDesc: string;
  /** Item leaf (ex. Produits) ou item menu sans sous-menu. */
  leafSelector: string;
  leafTitle: string;
  leafDesc: string;
  ensurePath: string;
  pathname: string;
  navigate: (to: string) => void;
  layoutShell?: TourLayoutShell | null;
  side?: 'top' | 'bottom' | 'left' | 'right';
};

/** Une ou deux étapes menu : parent sous-menu puis item, puis navigation. */
export function buildNavHighlightSteps(spec: NavHighlightSpec): DriveStep[] {
  const side = spec.side ?? 'right';
  const parent = spec.parentSelector?.trim();
  const leaf = spec.leafSelector.trim();
  const same = !parent || parent === leaf;

  if (same) {
    return [
      {
        element: leaf,
        popover: {
          title: spec.leafTitle,
          description: spec.leafDesc,
          side,
          align: 'start',
          onNextClick: (_e, _s, { driver: drv }) =>
            navThen(spec.ensurePath, spec.pathname, spec.navigate, drv, spec.layoutShell),
        },
      },
    ];
  }

  return [
    {
      element: parent!,
      popover: {
        title: spec.parentTitle,
        description: spec.parentDesc,
        side,
        align: 'start',
        onNextClick: (_e, _s, { driver: drv }) => {
          const leafEl = document.querySelector(leaf);
          if (leafEl) {
            let node: Element | null = leafEl;
            while (node && node !== document.body) {
              if (
                node.classList.contains('ant-menu-submenu') &&
                !node.classList.contains('ant-menu-submenu-open')
              ) {
                const title = node.querySelector('.ant-menu-submenu-title') as HTMLElement | null;
                title?.click();
                break;
              }
              node = node.parentElement;
            }
          }
          window.setTimeout(() => drv.moveNext(), 320);
        },
      },
    },
    {
      element: leaf,
      popover: {
        title: spec.leafTitle,
        description: spec.leafDesc,
        side,
        align: 'start',
        onNextClick: (_e, _s, { driver: drv }) =>
          navThen(spec.ensurePath, spec.pathname, spec.navigate, drv, spec.layoutShell),
      },
    },
  ];
}
